import { http } from "./http";

export type ReferralTrackingInput = {
  sellerId?: string;
  ref?: string;
  referralSource?: string;
};

export async function requestRegisterCode(
  email: string,
  referral?: ReferralTrackingInput
) {
  return http<{ ok: true; expiresInMinutes: number }>(`/auth/register/request-code`, {
    method: "POST",
    body: { email, ...referral },
  });
}

export async function verifyRegisterCode(email: string, code: string) {
  return http<{ ok: true }>(`/auth/register/verify-code`, {
    method: "POST",
    body: { email, code },
  });
}

export async function checkRegisterCpf(cpf: string) {
  return http<{ available: boolean }>(`/auth/register/check-cpf`, {
    method: "POST",
    body: { cpf },
  });
}

export async function register(
  email: string,
  password: string,
  code: string,
  payload: { fullName: string; cpf: string; phone: string } & ReferralTrackingInput
) {
  return http<void>(`/auth/register`, {
    method: "POST",
    body: { email, password, code, ...payload },
  });
}

export async function login(email: string, password: string) {
  return http<{ token: string }>(`/auth/login`, {
    method: "POST",
    body: { email, password },
  });
}

/**
 * Sessão aberta pelo suporte do admin interno (ENTER-07.1): `mode` diz se entrou como proprietário
 * ou como uma pessoa da equipe. `canWrite` diz se a sessão grava (admin geral) ou só consulta
 * (acesso temporário), SSW-03.1. `null` no login normal.
 */
export type SupportSession = {
  internalUserName: string;
  mode: "owner" | "member";
  canWrite: boolean;
};

export async function getMe(token: string) {
  return http<{
    id: string;
    email: string;
    fullName: string | null;
    cpf: string | null;
    phone?: string | null;
    mustChangePassword: boolean;
    supportSession?: SupportSession | null;
  }>(`/auth/me`, {
    token,
  });
}

export async function updateMe(
  token: string,
  payload: { fullName: string; cpf: string }
) {
  return http<{ ok: true }>(`/auth/me`, {
    method: "PUT",
    token,
    body: payload,
  });
}

export async function inviteUser(token: string, email: string) {
  return http<{
    id: string;
    email: string;
    status: "pending" | "accepted" | "canceled" | "expired";
    expiresAt: string;
    inviteUrl?: string;
  }>(`/auth/invite`, {
    method: "POST",
    token,
    body: { email },
  });
}

export type AuthInviteScope = "single_establishment" | "all_owner_establishments";

export type AuthInviteProfile =
  | "owner"
  | "admin"
  | "manager"
  | "professional"
  | "reception"
  | "dashboard_viewer"
  | "member";

export type AuthInviteRole = "owner" | "admin" | "member";

export type AuthInvite = {
  id: string;
  ownerId: string;
  establishmentId: string | null;
  providerId: string | null;
  invitedByUserId: string;
  invitedUserId: string | null;
  email: string;
  role: AuthInviteRole;
  accessProfileCode: AuthInviteProfile;
  accessScope: AuthInviteScope;
  permissionsJson: string | null;
  expiresAt: string;
  acceptedAt: string | null;
  canceledAt: string | null;
  lastSentAt: string;
  createdAt: string;
  updatedAt: string;
  status: "pending" | "accepted" | "canceled" | "expired";
};

export async function createAccessInvite(
  token: string,
  payload: {
    email: string;
    ownerId?: string;
    establishmentId?: string;
    providerId?: string;
    role?: AuthInviteRole;
    accessProfileCode?: AuthInviteProfile;
    accessScope?: AuthInviteScope;
    permissions?: Record<string, boolean>;
  }
) {
  return http<{
    id: string;
    email: string;
    status: "pending";
    expiresAt: string;
    inviteUrl?: string;
  }>(`/auth/invite`, {
    method: "POST",
    token,
    body: payload,
  });
}

export async function listAccessInvites(
  token: string,
  params?: { ownerId?: string; establishmentId?: string }
) {
  return http<AuthInvite[]>(`/auth/invites`, {
    token,
    query: params,
  });
}

export async function resendAccessInvite(
  token: string,
  inviteId: string,
  ownerId?: string
) {
  return http<{ ok: true; invite: AuthInvite; inviteUrl?: string }>(
    `/auth/invites/${inviteId}/resend`,
    {
      method: "POST",
      token,
      body: ownerId ? { ownerId } : {},
    }
  );
}

export async function cancelAccessInvite(
  token: string,
  inviteId: string,
  ownerId?: string
) {
  return http<{ ok: true }>(`/auth/invites/${inviteId}/cancel`, {
    method: "POST",
    token,
    body: ownerId ? { ownerId } : {},
  });
}

export async function acceptAccessInvite(payload: {
  token: string;
  password: string;
  fullName?: string;
}) {
  return http<{
    ok: true;
    userId: string;
    ownerId: string;
    establishmentIds: string[];
  }>(`/auth/invites/accept`, {
    method: "POST",
    body: payload,
  });
}

export async function validateAccessInvite(token: string) {
  return http<{
    ok: true;
    email: string;
    expiresAt: string;
  }>(`/auth/invites/validate`, {
    query: { token },
  });
}

export async function getMeAccess(token: string, establishmentId?: string) {
  return http<{
    establishmentId: string | null;
    access: {
      establishmentId: string;
      ownerId: string;
      userId: string;
      role: AuthInviteRole;
      accessProfileCode: AuthInviteProfile;
      accessScope: AuthInviteScope;
      permissions: Record<string, boolean>;
      source: "owner" | "membership";
    } | null;
    menu: {
      dashboard: boolean;
      units: boolean;
      personalization: boolean;
      availability: boolean;
      services: boolean;
      professionals: boolean;
      customFields: boolean;
      appointments: boolean;
      reports: boolean;
      customers: boolean;
      inventory: boolean;
      expenses: boolean;
      subscription: boolean;
      users: boolean;
      payments: boolean;
      customerSubscriptions: boolean;
      wallet: boolean;
      marketing: boolean;
      whatsapp: boolean;
      settings: boolean;
    } | null;
  }>(`/auth/me/access`, {
    token,
    query: establishmentId ? { establishmentId } : undefined,
  });
}

/**
 * Troca a senha de dentro da sessão. O backend derruba as outras sessões e
 * mantém a atual (D-04); se um dia a resposta trouxer `token`, ele passa a ser
 * o da sessão.
 */
export async function changePassword(
  token: string,
  payload: { currentPassword?: string; newPassword: string }
) {
  return http<{ ok: true; token?: string }>(`/auth/change-password`, {
    method: "POST",
    token,
    body: payload,
  });
}

export async function requestPasswordReset(email: string) {
  return http<{ ok: true; expiresInMinutes: number }>(`/auth/password/request-reset`, {
    method: "POST",
    body: { email },
  });
}

export async function verifyPasswordReset(email: string, code: string) {
  return http<{ ok: true }>(`/auth/password/verify-reset`, {
    method: "POST",
    body: { email, code },
  });
}

export async function resetPassword(email: string, code: string, newPassword: string) {
  return http<{ ok: true }>(`/auth/password/reset`, {
    method: "POST",
    body: { email, code, newPassword },
  });
}
